/**
 * Prismotor — viser foreslåede pris-/antal-opdateringer på DORA-fakturalinjer.
 * Godkendelse skriver med det samme til DORA via PUT.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Grid, IconButton,
  MenuItem, Snackbar, Switch, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography, Alert, Tooltip, FormControlLabel, Checkbox, Stack, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { api } from '../../services/api';
import type { PriceEngineSuggestion, PriceEngineSuggestionStatus, PriceEngineCustomerPrice } from '../../types';

const fmtMoney = (n: string | number | null | undefined) => {
  if (n == null) return '—';
  const v = typeof n === 'string' ? parseFloat(n) : n;
  return v.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr.';
};
const fmtQty = (n: string | number | null | undefined) => {
  if (n == null) return '—';
  const v = typeof n === 'string' ? parseFloat(n) : n;
  return v.toLocaleString('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const sourceLabel: Record<string, string> = {
  CUSTOMER: 'Kundepris',
  PRICE_GROUP: 'Prisgruppe',
  LOCATION: 'Lokationspris',
  TIME_TRACKING: 'Time-tracking',
};
const statusColor: Record<PriceEngineSuggestionStatus, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  PENDING: 'warning', APPROVED: 'primary', APPLIED: 'success', REJECTED: 'default', STALE: 'default', FAILED: 'error',
};

export default function PriceEnginePage() {
  const [config, setConfig] = useState<{ priceEngineEnabled: boolean; priceEngineTimeRateProductNumbers: string[] } | null>(null);
  const [suggestions, setSuggestions] = useState<PriceEngineSuggestion[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('PENDING');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [working, setWorking] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [customerPrices, setCustomerPrices] = useState<PriceEngineCustomerPrice[]>([]);
  const [editCp, setEditCp] = useState<Partial<PriceEngineCustomerPrice> | null>(null);
  const [cpSearch, setCpSearch] = useState('');
  // Timesats-multiselect er pauset — håndteres i mobil-app'en. Bagvedliggende
  // scheduler-logik er intakt og aktiveres når priceEngineTimeRateProductNumbers
  // får varer igen.
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'success',
  });

  const reload = async () => {
    setLoading(true);
    try {
      const [cfg, list, cps] = await Promise.all([
        api.getPriceEngineConfig(),
        api.getPriceEngineSuggestions(statusFilter),
        api.getPriceEngineCustomerPrices(),
      ]);
      setConfig(cfg);
      setSuggestions(list);
      setCustomerPrices(cps);
      setSelected(new Set());
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Kunne ikke hente data';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const saveCustomerPrice = async () => {
    if (!editCp) return;
    const cn = (editCp.customerNumber || '').trim();
    const pn = (editCp.productNumber || '').trim();
    const price = Number(editCp.price);
    if (!cn || !pn || !isFinite(price)) {
      setSnackbar({ open: true, message: 'Kundenummer, varenummer og pris er påkrævet', severity: 'error' });
      return;
    }
    setWorking(true);
    try {
      await api.upsertPriceEngineCustomerPrice({
        customerNumber: cn,
        customerName: editCp.customerName ?? null,
        productNumber: pn,
        productName: editCp.productName ?? null,
        price,
        unitName: editCp.unitName ?? null,
      });
      setEditCp(null);
      const cps = await api.getPriceEngineCustomerPrices();
      setCustomerPrices(cps);
      setSnackbar({ open: true, message: 'Gemt', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e instanceof Error ? e.message : 'Kunne ikke gemme', severity: 'error' });
    } finally { setWorking(false); }
  };

  const deleteCustomerPrice = async (id: string) => {
    if (!confirm('Slet denne kundepris?')) return;
    setWorking(true);
    try {
      await api.deletePriceEngineCustomerPrice(id);
      setCustomerPrices(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      setSnackbar({ open: true, message: e instanceof Error ? e.message : 'Sletning fejlede', severity: 'error' });
    } finally { setWorking(false); }
  };

  const customerPricesByCustomer = useMemo(() => {
    const q = cpSearch.trim().toLowerCase();
    const filtered = q
      ? customerPrices.filter(p =>
          p.customerNumber.toLowerCase().includes(q) ||
          (p.customerName || '').toLowerCase().includes(q) ||
          p.productNumber.toLowerCase().includes(q) ||
          (p.productName || '').toLowerCase().includes(q),
        )
      : customerPrices;
    const grouped = new Map<string, { customerNumber: string; customerName: string | null; rows: PriceEngineCustomerPrice[] }>();
    for (const p of filtered) {
      const key = p.customerNumber;
      let g = grouped.get(key);
      if (!g) { g = { customerNumber: p.customerNumber, customerName: p.customerName, rows: [] }; grouped.set(key, g); }
      g.rows.push(p);
    }
    return Array.from(grouped.values()).sort((a, b) =>
      (a.customerName || a.customerNumber).localeCompare(b.customerName || b.customerNumber, 'da'),
    );
  }, [customerPrices, cpSearch]);

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter]);

  const handleToggleEnabled = async (enabled: boolean) => {
    setWorking(true);
    try {
      const c = await api.updatePriceEngineConfig({ priceEngineEnabled: enabled });
      setConfig(c);
    } catch (e) {
      setSnackbar({ open: true, message: e instanceof Error ? e.message : 'Kunne ikke gemme', severity: 'error' });
    } finally { setWorking(false); }
  };


  const handleScanNow = async () => {
    setScanning(true);
    try {
      const r = await api.scanPriceEngineNow();
      setSnackbar({ open: true, message: `Scannet ${r.scanned} cargos · ${r.suggestions} forslag oprettet/opdateret`, severity: 'info' });
      await reload();
    } catch (e) {
      setSnackbar({ open: true, message: e instanceof Error ? e.message : 'Scan fejlede', severity: 'error' });
    } finally { setScanning(false); }
  };

  const handleApprove = async (id: string) => {
    setWorking(true);
    try {
      await api.approvePriceEngineSuggestion(id);
      setSnackbar({ open: true, message: 'Skrevet til DORA', severity: 'success' });
      await reload();
    } catch (e) {
      setSnackbar({ open: true, message: e instanceof Error ? e.message : 'Godkendelse fejlede', severity: 'error' });
    } finally { setWorking(false); }
  };

  const handleReject = async (id: string) => {
    setWorking(true);
    try {
      await api.rejectPriceEngineSuggestion(id);
      await reload();
    } catch (e) {
      setSnackbar({ open: true, message: e instanceof Error ? e.message : 'Afvisning fejlede', severity: 'error' });
    } finally { setWorking(false); }
  };

  const handleBulkApprove = async () => {
    if (selected.size === 0) return;
    setWorking(true);
    try {
      const r = await api.bulkApprovePriceEngineSuggestions(Array.from(selected));
      setSnackbar({
        open: true,
        message: `${r.ok} godkendt · ${r.failed} fejlede`,
        severity: r.failed > 0 ? 'error' : 'success',
      });
      await reload();
    } catch (e) {
      setSnackbar({ open: true, message: e instanceof Error ? e.message : 'Bulk-godkendelse fejlede', severity: 'error' });
    } finally { setWorking(false); }
  };

  const allSelected = useMemo(() => {
    const pendingIds = suggestions.filter(s => s.status === 'PENDING').map(s => s.id);
    return pendingIds.length > 0 && pendingIds.every(id => selected.has(id));
  }, [suggestions, selected]);

  const togglePendingAll = () => {
    const pending = suggestions.filter(s => s.status === 'PENDING').map(s => s.id);
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(pending));
  };
  const toggleOne = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (loading && !config) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Prismotor</Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card><CardContent sx={{ pb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Aktivering</Typography>
            <FormControlLabel
              control={<Switch checked={config?.priceEngineEnabled ?? false}
                onChange={(e) => handleToggleEnabled(e.target.checked)} disabled={working} />}
              label={config?.priceEngineEnabled ? 'Aktiveret — scanner hver 10. min.' : 'Slået fra'}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Motoren matcher DORA-cargo-stops mod jeres salgssteder, foreslår pris-/antalsopdateringer.
              Godkendelse skriver direkte til DORA via PUT.
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button variant="outlined" size="small" startIcon={<PlayArrowIcon />}
                onClick={handleScanNow} disabled={scanning || !config?.priceEngineEnabled}>
                {scanning ? <CircularProgress size={16} /> : 'Scan nu'}
              </Button>
              <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={reload}>
                Genopfrisk
              </Button>
            </Box>
          </CardContent></Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card><CardContent sx={{ pb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Timesats-varer</Typography>
            <Alert severity="info" sx={{ mb: 1 }}>
              Pauset — antals-justering ud fra time-tracking håndteres for nu i mobil-app'en.
              Funktionaliteten kan genaktiveres her hvis behovet opstår igen.
            </Alert>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Scheduleren springer time-aspektet over så længe listen er tom — pris-aspektet
              kører uændret.
            </Typography>
          </CardContent></Card>
        </Grid>

        <Grid item xs={12}>
          <Card><CardContent sx={{ pb: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>Forslag</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField select size="small" label="Status" value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 140 }}>
                  {['PENDING', 'APPLIED', 'REJECTED', 'STALE', 'FAILED', 'ALL'].map(s =>
                    <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
                {selected.size > 0 && (
                  <Button size="small" variant="contained" startIcon={<CheckIcon />}
                    onClick={handleBulkApprove} disabled={working}>
                    Godkend valgte ({selected.size})
                  </Button>
                )}
              </Stack>
            </Stack>
            <Divider sx={{ mb: 1 }} />
            {suggestions.length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ py: 3, textAlign: 'center' }}>
                Ingen forslag i denne status.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={allSelected} onChange={togglePendingAll}
                        indeterminate={selected.size > 0 && !allSelected} />
                    </TableCell>
                    <TableCell>Cargo</TableCell>
                    <TableCell>Kunde</TableCell>
                    <TableCell>Lokation</TableCell>
                    <TableCell>Vare</TableCell>
                    <TableCell align="right">Nuv. pris</TableCell>
                    <TableCell align="right">Foreslået</TableCell>
                    <TableCell align="right">Nuv. antal</TableCell>
                    <TableCell align="right">Foreslået</TableCell>
                    <TableCell>Kilde</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Handling</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suggestions.map(s => {
                    const priceDelta = s.kind === 'PRICE' && s.suggestedAmount != null
                      ? parseFloat(s.suggestedAmount) - parseFloat(s.currentAmount) : 0;
                    return (
                      <TableRow key={s.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={selected.has(s.id)}
                            onChange={() => toggleOne(s.id)} disabled={s.status !== 'PENDING'} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontWeight={600}>{s.cargoRef}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{s.customerName || s.customerNumber || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={s.matchExplanation || ''}>
                            <Typography variant="caption">{s.salesLocationName || '—'}</Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontWeight={500}>{s.productNumber}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {s.productName || ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{fmtMoney(s.currentAmount)}</TableCell>
                        <TableCell align="right" sx={{ color: priceDelta < 0 ? 'success.main' : priceDelta > 0 ? 'warning.main' : 'inherit', fontWeight: 600 }}>
                          {s.kind === 'PRICE' ? fmtMoney(s.suggestedAmount) : '—'}
                        </TableCell>
                        <TableCell align="right">{fmtQty(s.currentQty)} {s.unitName || ''}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {s.kind === 'QTY_TIME' ? `${fmtQty(s.suggestedQty)} ${s.unitName || ''}` : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={sourceLabel[s.source] || s.source} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={s.status} color={statusColor[s.status]} />
                          {s.errorMessage && (
                            <Tooltip title={s.errorMessage}>
                              <Typography variant="caption" color="error" sx={{ display: 'block', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {s.errorMessage}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {s.status === 'PENDING' && (
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Godkend og send til DORA">
                                <IconButton size="small" color="success"
                                  onClick={() => handleApprove(s.id)} disabled={working}>
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Afvis">
                                <IconButton size="small" onClick={() => handleReject(s.id)} disabled={working}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Card><CardContent sx={{ pb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>Generelle kundepriser</Typography>
              <Typography variant="caption" color="text.secondary">
                For varer der ikke ligger i Produktsalg — fx kørselstakster, container-leje, mandskab.
                Bruges som fallback når motoren ikke finder en lokationspris.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <TextField size="small" placeholder="Søg kunde/vare" value={cpSearch}
                onChange={(e) => setCpSearch(e.target.value)} sx={{ minWidth: 220 }} />
              <Button size="small" variant="contained" startIcon={<AddIcon />}
                onClick={() => setEditCp({})}>Ny kundepris</Button>
            </Stack>
          </Stack>
          <Divider sx={{ mb: 1 }} />
          {customerPricesByCustomer.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ py: 3, textAlign: 'center' }}>
              Ingen kundepriser oprettet endnu.
            </Typography>
          ) : (
            customerPricesByCustomer.map(g => (
              <Accordion key={g.customerNumber} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" fontWeight={600}>
                    {g.customerName || g.customerNumber}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({g.customerNumber} · {g.rows.length} varer)
                    </Typography>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Varenr.</TableCell>
                        <TableCell>Navn</TableCell>
                        <TableCell>Enhed</TableCell>
                        <TableCell align="right">Pris</TableCell>
                        <TableCell align="right">Handling</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {g.rows.map(r => (
                        <TableRow key={r.id} hover>
                          <TableCell><Typography variant="caption" fontWeight={500}>{r.productNumber}</Typography></TableCell>
                          <TableCell><Typography variant="caption">{r.productName || '—'}</Typography></TableCell>
                          <TableCell><Typography variant="caption">{r.unitName || '—'}</Typography></TableCell>
                          <TableCell align="right">{fmtMoney(r.price)}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <IconButton size="small" onClick={() => setEditCp(r)} disabled={working}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => deleteCustomerPrice(r.id)} disabled={working}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </CardContent></Card>
      </Box>

      <Dialog open={!!editCp} onClose={() => setEditCp(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editCp?.id ? 'Rediger kundepris' : 'Ny kundepris'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Kundenummer" size="small" value={editCp?.customerNumber || ''}
              onChange={(e) => setEditCp(p => ({ ...p, customerNumber: e.target.value }))}
              disabled={!!editCp?.id} required />
            <TextField label="Kundenavn (valgfri)" size="small" value={editCp?.customerName || ''}
              onChange={(e) => setEditCp(p => ({ ...p, customerName: e.target.value }))} />
            <TextField label="Varenummer" size="small" value={editCp?.productNumber || ''}
              onChange={(e) => setEditCp(p => ({ ...p, productNumber: e.target.value }))}
              disabled={!!editCp?.id} required />
            <TextField label="Varenavn (valgfri)" size="small" value={editCp?.productName || ''}
              onChange={(e) => setEditCp(p => ({ ...p, productName: e.target.value }))} />
            <TextField label="Enhed (valgfri)" size="small" value={editCp?.unitName || ''}
              onChange={(e) => setEditCp(p => ({ ...p, unitName: e.target.value }))} />
            <TextField label="Pris" size="small" type="number" value={editCp?.price ?? ''}
              onChange={(e) => setEditCp(p => ({ ...p, price: e.target.value }))} required />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCp(null)}>Annullér</Button>
          <Button variant="contained" onClick={saveCustomerPrice} disabled={working}>Gem</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
